// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Location } from '@angular/common';
import { Injectable, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Params,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { BehaviorSubject, ReplaySubject, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
} from 'rxjs/operators';
import { ScreenLayoutService } from '../../shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from '../../shared/utils/api-call-state.util';

function paramsFromRouteSnapshot(
  route: DeepReadonly<ActivatedRouteSnapshot | null>,
) {
  let allParams: Params = {};
  while (route) {
    allParams = { ...allParams, ...route.params, ...route.queryParams };
    route = route.firstChild;
  }
  return allParams as Readonly<Params>;
}

function getFullRouteData(
  route: DeepReadonly<ActivatedRouteSnapshot | null>,
): Record<string, unknown> {
  return {
    ...route?.data,
    ...route?.children.reduce(
      (acc, child) => ({ ...acc, ...getFullRouteData(child) }),
      {},
    ),
  };
}

function isSameDeep(
  prev: Readonly<Params> | undefined,
  curr: Readonly<Params> | undefined,
): boolean {
  return JSON.stringify(prev) == JSON.stringify(curr);
}

export interface SearchParams {
  // query string from user
  q: string;
}
/**
 * extract search state from params
 * it is stored in the URl currently only
 * q = query is there, add things like filters
 * here
 */
export function searchStateFromParams(
  params?: Readonly<Params>,
): SearchParams | undefined {
  if (params) {
    if ('q' in params) {
      return {
        q: params?.q,
      };
    }
  }
  return undefined;
}
/**
 * if ?q='string' -> search is open (including q='')
 * if ?q= undefined -> search is closed
 */
@Injectable({
  providedIn: 'root',
})
export class RouteService {
  // get all params from parent routes as well (wtf angular)
  // this will likely cause problems of param names are not unique
  #routeParams$ = new BehaviorSubject<Readonly<Params> | undefined>(undefined);
  routeParams$ = this.#routeParams$.pipe(distinctUntilChanged(isSameDeep));
  #queryParams$ = new BehaviorSubject<Readonly<Params> | undefined>(undefined);
  queryParams$ = this.#queryParams$.pipe(distinctUntilChanged(isSameDeep));

  routeData$ = this.#routeParams$.pipe(
    map(() => getFullRouteData(this.route.snapshot)),
  );

  // only set if the page is called up with search params already set
  #initialQueryParams$ = new ReplaySubject<Readonly<Params>>(1);
  initialQueryParams$ = this.#initialQueryParams$.pipe(
    distinctUntilChanged(isSameDeep),
  );

  // false or current systemIds
  currentSystem$ = this.routeParams$
    .pipe(
      map((parameters: Params | undefined) => {
        const systemSerialNumber = parameters?.systemSerialNumber;
        const systemMaterialNumber = parameters?.systemMaterialNumber;
        return systemSerialNumber && systemMaterialNumber
          ? {
              systemSerialNumber: String(systemSerialNumber),
              systemMaterialNumber: parseInt(systemMaterialNumber),
            }
          : false;
      }),
    )
    .pipe(
      distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev) == JSON.stringify(curr);
      }),
    );

  currentComponent$ = this.routeParams$
    .pipe(
      map((parameters: Params | undefined) => {
        const componentId = parseInt(parameters?.component);
        return componentId ? componentId : false;
      }),
    )
    .pipe(
      distinctUntilChanged((prev, curr) => {
        return JSON.stringify(prev) == JSON.stringify(curr);
      }),
    );

  // figure out whether we are on a sub page
  isOnSubPage$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(undefined),
    map(() => {
      const subPage = !!this.router.url.match(/system\/.*\/.*\/.*/);
      const settingsPage = !!this.router.url.match(/settings/);
      return subPage || settingsPage;
    }),
  );

  // is the search page open?
  searchPageOpen$ = this.queryParams$.pipe(
    map((params) => {
      return params?.q !== undefined;
    }),
  );

  // current search string (pulled from query string 'q' in route)
  searchState$ = this.queryParams$.pipe(map(searchStateFromParams));
  // used to get current search state. we provide this as a property here
  // for easier access, so search pane does not have to store it itself
  searchState?: SearchParams;
  searchString$ = this.searchState$.pipe(
    map((s) => ((s ? s?.q : '') || '').trim()),
  );

  hideAppFooter$ = this.routeData$.pipe(map((data) => data.hideAppFooter));

  hideSystemDetails$ = this.routeData$.pipe(
    map((data) => data.hideSystemDetails),
  );

  // should we show the back button in the header?
  backButtonVisible$ = combineLatest([
    this.isOnSubPage$.pipe(startWith(false)),
    this.searchPageOpen$.pipe(startWith(false)),
    this.hideSystemDetails$.pipe(startWith(false)),
    this.hideAppFooter$.pipe(startWith(false)),
  ]).pipe(
    map((conds) => {
      return conds.some((c) => !!c);
    }),
  );

  backButtonIcon$ = of('arrow-line-left-s');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private screenLayoutService: ScreenLayoutService,
    private location: Location,
  ) {
    // for initial query params (wtf angular)
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(
          (event): event is RoutesRecognized =>
            !!event && event instanceof RoutesRecognized,
        ),
        map((event) => searchStateFromParams(event.state.root.queryParams)),
        take(1),
      )
      .subscribe((params) => {
        if (params) {
          this.#queryParams$.next(params);
          this.#initialQueryParams$.next(params);
        }
      });

    // for subsequent changes (wtf angular)
    this.route.queryParams
      .pipe(takeUntilDestroyed())
      .subscribe((params) => this.#queryParams$.next(params));

    // get initial params
    this.#routeParams$.next(
      paramsFromRouteSnapshot(this.router.routerState.snapshot.root),
    );
    // get subsequent params
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
      )
      .subscribe(() => {
        this.#routeParams$.next(paramsFromRouteSnapshot(route.snapshot.root));
      });

    this.searchState$.subscribe((state) => (this.searchState = state));
  }

  pressBackButton(): void {
    const actions = {
      closeSearch: () => this.closeSearch(),
      backToOverview: () => this.backToOverview(),
      browserBack: () => this.location.back(),
    };

    const action = this.backAction();
    actions[action]();
  }

  openSearch(clear = true, focus = false): void {
    if (clear) {
      this.setSearchString('');
    } else {
      // only set '' in case there is not already a search string
      this.searchState$.pipe(take(1)).subscribe((current) => {
        if (!current) {
          this.setSearchString('');
        }
      });
    }
    if (focus) {
      document.getElementById('searchInput')?.focus();
    }
  }

  setSearchString(searchString = ''): void {
    this.router.navigate([], {
      replaceUrl: true,
      //skipLocationChange: true,
      queryParams: {
        q: searchString,
      },
    });
  }

  closeSearch(): void {
    this.router.navigate([], {
      replaceUrl: true,
      //skipLocationChange: true,
      queryParams: {
        q: undefined,
      },
    });
  }

  goToSystem({
    systemSerialNumber,
    systemMaterialNumber,
  }: {
    systemSerialNumber: string;
    systemMaterialNumber: number;
  }): void {
    this.router.navigate(['/system', systemMaterialNumber, systemSerialNumber]);
  }

  backToOverview(): void {
    this.goToSystem(
      this.params as Readonly<{
        systemSerialNumber: string;
        systemMaterialNumber: number;
      }>,
    );
  }

  openSettings(): void {
    if (this.screenLayoutService.desktopAny$.getValue()) {
      document.getElementById('settignsDrawer')!.setAttribute('visible', '');
    } else {
      this.router.navigate(['/settings']);
    }
  }

  get params(): Readonly<{
    systemSerialNumber?: string;
    systemMaterialNumber?: number;
  }> {
    return paramsFromRouteSnapshot(this.route.snapshot);
  }

  // Signal versions for the computed logic
  protected searchPageOpenSignal = toSignal(this.searchPageOpen$, {
    initialValue: false,
  });

  // Computed signal that determines what action to take when back button is pressed
  protected backAction = computed(() => {
    const searchOpen = this.searchPageOpenSignal();

    if (searchOpen) {
      return 'closeSearch';
    }else {
      return 'browserBack';
    }
  });
}
